import React, { useCallback, useEffect, useState } from "react";
import { Landmark, Plus, Trash2, Edit3, X, Loader2, CheckCircle2, Info } from "lucide-react";
import { portalApi } from "../../lib/portalApi";

const STATUS_META = {
  not_submitted: { label: "Not submitted", cls: "bg-[#7A6B5F]/15 text-[#7A6B5F]" },
  submitted: { label: "Submitted", cls: "bg-[#6E1F2C]/10 text-[#6E1F2C]" },
  under_review: { label: "Under review", cls: "bg-[#A6822F]/15 text-[#A6822F]" },
  comments_received: { label: "Comments received", cls: "bg-[#A6822F]/20 text-[#8A6A20]" },
  resubmitted: { label: "Resubmitted", cls: "bg-[#6E1F2C]/15 text-[#6E1F2C]" },
  approved: { label: "Approved", cls: "bg-emerald-600/15 text-emerald-700" },
  received: { label: "Received", cls: "bg-emerald-600/15 text-emerald-700" },
  rejected: { label: "Rejected", cls: "bg-[#A3141A]/15 text-[#A3141A]" },
  on_hold: { label: "On hold", cls: "bg-[#7A6B5F]/20 text-[#3E2A22]" },
};
const DELAY_LABEL = {
  authority: "Authority / Government",
  property_management: "Property Management",
  third_party_contractor: "Third-Party Contractor",
  client_decision: "Client Decision",
  force_majeure: "Force Majeure",
};
const CATEGORY_LABEL = {
  authority_approval: "Authority Approval",
  property_management: "Property Management",
};
const EMPTY_FORM = {
  name: "", responsible_party: "", submission_date: "", resubmission_date: "",
  received_date: "", status: "not_submitted", reference_number: "",
  delay_classification: "authority", remarks: "", category: "authority_approval",
};

const inputCls = "mt-1 w-full bg-[#FAF5EC] border border-[#6E1F2C]/20 px-3 py-2 text-xs focus:outline-none focus:border-[#6E1F2C]";

const F = ({ label, children }) => (
  <label className="block">
    <span className="text-[9px] tracking-[0.24em] uppercase text-[#7A6B5F]">{label}</span>
    {children}
  </label>
);

function ApprovalModal({ open, initial, slug, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (open) {
      setErr("");
      setForm(initial ? {
        ...EMPTY_FORM,
        ...Object.fromEntries(Object.keys(EMPTY_FORM).map((k) => [k, initial[k] ?? ""])),
        status: initial.status || "not_submitted",
        delay_classification: initial.delay_classification || "authority",
        category: initial.category || "authority_approval",
      } : EMPTY_FORM);
    }
  }, [open, initial]);

  if (!open) return null;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    const body = Object.fromEntries(Object.entries(form).map(([k, v]) => [k, v === "" ? null : v]));
    try {
      if (initial) await portalApi.patchApproval(slug, initial.id, body);
      else await portalApi.createApproval(slug, body);
      onSaved();
      onClose();
    } catch (ex) { setErr(ex.message || "Save failed"); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="portal-approval-modal">
      <form onSubmit={save} className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-serif-display text-xl text-[#1C110C]">{initial ? "Edit approval item" : "Add approval item"}</h3>
          <button type="button" onClick={onClose} data-testid="portal-approval-modal-close"><X size={18} /></button>
        </div>
        <F label="Approval name *">
          <input value={form.name} onChange={set("name")} required className={inputCls} data-testid="portal-approval-name-input" placeholder="e.g. Nakheel NOC" />
        </F>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <F label="Responsible party">
            <input value={form.responsible_party} onChange={set("responsible_party")} className={inputCls} data-testid="portal-approval-responsible-input" placeholder="e.g. Trakhees" />
          </F>
          <F label="Reference number">
            <input value={form.reference_number} onChange={set("reference_number")} className={inputCls} data-testid="portal-approval-ref-input" />
          </F>
          <F label="Submission date">
            <input type="date" value={form.submission_date || ""} onChange={set("submission_date")} className={inputCls} data-testid="portal-approval-submission-input" />
          </F>
          <F label="Resubmission date (if any)">
            <input type="date" value={form.resubmission_date || ""} onChange={set("resubmission_date")} className={inputCls} data-testid="portal-approval-resubmission-input" />
          </F>
          <F label="Received / approved date">
            <input type="date" value={form.received_date || ""} onChange={set("received_date")} className={inputCls} data-testid="portal-approval-received-input" />
          </F>
          <F label="Status">
            <select value={form.status} onChange={set("status")} className={inputCls} data-testid="portal-approval-status-select">
              {Object.entries(STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </F>
          <F label="Delay classification">
            <select value={form.delay_classification} onChange={set("delay_classification")} className={inputCls} data-testid="portal-approval-delay-select">
              {Object.entries(DELAY_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </F>
          <F label="Category">
            <select value={form.category} onChange={set("category")} className={inputCls} data-testid="portal-approval-category-select">
              {Object.entries(CATEGORY_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </F>
        </div>
        <F label="One-line remarks">
          <input value={form.remarks} onChange={set("remarks")} className={inputCls} data-testid="portal-approval-remarks-input" placeholder="e.g. Awaiting authority comments — 2nd resubmission" />
        </F>
        {err && <p className="text-xs text-[#A3141A]" data-testid="portal-approval-modal-error">{err}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="border border-[#6E1F2C]/30 text-[#6E1F2C] px-4 py-2 text-[10px] tracking-[0.24em] uppercase">Cancel</button>
          <button type="submit" disabled={busy} data-testid="portal-approval-save-btn"
            className="bg-[#6E1F2C] text-[#FAF5EC] px-5 py-2 text-[10px] tracking-[0.24em] uppercase disabled:opacity-60 inline-flex items-center gap-2">
            {busy && <Loader2 size={12} className="animate-spin" />} Save
          </button>
        </div>
      </form>
    </div>
  );
}

function ApprovalRow({ a, canManage, onEdit, onDelete }) {
  const done = ["approved", "received"].includes(a.status) || !!a.received_date;
  const sm = STATUS_META[a.status] || STATUS_META.not_submitted;
  return (
    <tr className="border-b border-[#6E1F2C]/10 hover:bg-[#FAF5EC]/50" data-testid={`portal-approval-row-${a.id}`}>
      <td className="py-2.5 pr-3">
        <p className="text-xs text-[#1C110C] font-medium flex items-center gap-1.5">
          {done && <CheckCircle2 size={13} className="text-emerald-600 shrink-0" />}{a.name}
        </p>
        <p className="text-[10px] text-[#7A6B5F]">{CATEGORY_LABEL[a.category] || "Authority Approval"}</p>
      </td>
      <td className="py-2.5 pr-3 text-[11px] text-[#3E2A22]">{a.responsible_party || "—"}</td>
      <td className="py-2.5 pr-3 text-[11px] text-[#3E2A22] whitespace-nowrap">{a.submission_date || "—"}</td>
      <td className="py-2.5 pr-3 text-[11px] text-[#3E2A22] whitespace-nowrap">{a.resubmission_date || "—"}</td>
      <td className="py-2.5 pr-3 text-[11px] text-[#3E2A22] whitespace-nowrap">{a.received_date || "—"}</td>
      <td className="py-2.5 pr-3">
        <span className={`inline-block text-[9px] tracking-[0.18em] uppercase px-2 py-1 ${sm.cls}`} data-testid={`portal-approval-status-${a.id}`}>{sm.label}</span>
      </td>
      <td className="py-2.5 pr-3 text-[11px] text-[#3E2A22]">{a.reference_number || "—"}</td>
      <td className="py-2.5 pr-3 text-[10px] text-[#7A6B5F]">{DELAY_LABEL[a.delay_classification] || "Authority / Government"}</td>
      <td className="py-2.5 pr-3 text-[11px] text-[#3E2A22] max-w-[220px]"><span className="line-clamp-2">{a.remarks || "—"}</span></td>
      {canManage && (
        <td className="py-2.5 whitespace-nowrap">
          <button onClick={() => onEdit(a)} data-testid={`portal-approval-edit-${a.id}`} className="text-[#6E1F2C] hover:text-[#56151F] mr-2" title="Edit"><Edit3 size={13} /></button>
          <button onClick={() => onDelete(a)} data-testid={`portal-approval-delete-${a.id}`} className="text-[#A3141A]/70 hover:text-[#A3141A]" title="Delete"><Trash2 size={13} /></button>
        </td>
      )}
    </tr>
  );
}

export default function ApprovalsTab({ slug, canManage, isAdmin }) {
  const [data, setData] = useState(null);
  const [modal, setModal] = useState({ open: false, initial: null });
  const [busy, setBusy] = useState("");

  const load = useCallback(() => {
    portalApi.listApprovals(slug).then(setData).catch(() => setData({ approvals: [], summary: {} }));
  }, [slug]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="text-[11px] text-[#7A6B5F] py-6">Loading approvals…</div>;
  const items = data.approvals || [];
  const summary = data.summary || {};

  const del = async (a) => {
    if (!window.confirm(`Delete approval item "${a.name}"?\n\nThis only removes this checklist row — no updates, photos, reports, files or financials are affected.`)) return;
    try { await portalApi.deleteApproval(slug, a.id); load(); }
    catch (e) { window.alert(e.message); }
  };
  const seed = async () => {
    setBusy("seed");
    try {
      const r = await portalApi.seedApprovals(slug);
      load();
      window.alert(`${r.created_count} standard approval item(s) added.`);
    } catch (e) { window.alert(e.message); }
    setBusy("");
  };

  return (
    <div data-testid="portal-approvals-tab" className="space-y-6 pb-24 lg:pb-0">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="text-[10px] tracking-[0.32em] uppercase text-[#6E1F2C] inline-flex items-center gap-1.5">
            <Landmark size={11} /> Authority / Third-Party Dependencies
          </p>
          <h2 className="font-serif-display text-2xl text-[#1C110C] mt-1">Approval Tracker</h2>
          {summary.total > 0 && (
            <p className="text-[11px] text-[#7A6B5F] mt-1" data-testid="portal-approvals-summary">
              <b className="text-[#1C110C]">{summary.completed}/{summary.total}</b> approvals received · authority progress <b className="text-[#6E1F2C]">{summary.percent}%</b>
            </p>
          )}
        </div>
        {canManage && (
          <div className="flex items-center gap-2">
            {items.length > 0 && (
              <button onClick={seed} disabled={!!busy} data-testid="portal-approvals-seed-btn"
                className="border border-[#A6822F]/50 text-[#A6822F] px-3 py-2 text-[10px] tracking-[0.24em] uppercase hover:bg-[#A6822F] hover:text-white transition-colors disabled:opacity-50">
                Add standard items
              </button>
            )}
            <button onClick={() => setModal({ open: true, initial: null })} data-testid="portal-approvals-add-btn"
              className="inline-flex items-center gap-2 bg-[#6E1F2C] text-[#FAF5EC] px-3 py-2 text-[10px] tracking-[0.24em] uppercase hover:bg-[#56151F]">
              <Plus size={13} /> Add approval
            </button>
          </div>
        )}
      </div>

      <div className="bg-[#FAF5EC] border-l-2 border-[#A6822F] px-4 py-3 text-[11px] text-[#3E2A22] flex gap-2" data-testid="portal-approvals-note">
        <Info size={13} className="text-[#A6822F] shrink-0 mt-0.5" />
        <span>{summary.note || "Approval-stage delays are government / authority / property-management dependencies and are not contractor-attributable physical execution delays. This tracker feeds the Authority Approvals progress only — physical execution progress is measured separately from site tasks."}</span>
      </div>

      {items.length === 0 ? (
        <div className="bg-white border border-dashed border-[#6E1F2C]/25 p-12 text-center" data-testid="portal-approvals-empty">
          <Landmark className="mx-auto text-[#D4A963] mb-3" size={36} />
          <p className="text-sm text-[#3E2A22]">No approval items tracked yet.</p>
          {canManage && (
            <button onClick={seed} disabled={!!busy} data-testid="portal-approvals-seed-empty-btn"
              className="mt-4 border border-[#6E1F2C] text-[#6E1F2C] px-4 py-2 text-[10px] tracking-[0.24em] uppercase hover:bg-[#6E1F2C] hover:text-white transition-colors disabled:opacity-50">
              {busy === "seed" ? "Adding…" : "Add standard Dubai approvals (NOC / DCD / Trakhees / Permits)"}
            </button>
          )}
        </div>
      ) : (
        <div className="bg-white border border-[#6E1F2C]/15 p-4 overflow-x-auto">
          <table className="w-full text-left min-w-[980px]" data-testid="portal-approvals-table">
            <thead>
              <tr className="text-[9px] tracking-[0.22em] uppercase text-[#7A6B5F]">
                {["Approval", "Responsible", "Submitted", "Resubmitted", "Received", "Status", "Ref no.", "Delay class", "Remarks"].map((h) => (
                  <th key={h} className="pb-2 pr-3 border-b border-[#6E1F2C]/15 font-medium">{h}</th>
                ))}
                {canManage && <th className="pb-2 border-b border-[#6E1F2C]/15" />}
              </tr>
            </thead>
            <tbody>
              {items.map((a) => (
                <ApprovalRow key={a.id} a={a} canManage={canManage}
                  onEdit={(x) => setModal({ open: true, initial: x })} onDelete={del} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ApprovalModal open={modal.open} initial={modal.initial} slug={slug}
        onClose={() => setModal({ open: false, initial: null })} onSaved={load} />
    </div>
  );
}
